import { useState, useRef, useEffect, useMemo } from "react";
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
  useQueryClient,
  UseQueryResult
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { DataUrlItem, Step } from "../../types";
import { findDataUrls, set, uploadLinks, validateHousing } from "../../helpers";
import { Aside } from "../Aside";
import { IframeLayout } from "../IframeLayout";
import { toast } from "react-toastify";
import {
  Application,
  CheckoutSubmissionType,
  CombinedIncomeVerificationSchema,
  IncomeStatus,
  IncomeType,
  SubmissionType,
  Verification,
  VerificationNames
} from "shared/types";
import { getErrorMessage } from "utils/error";
import {
  useCurrentSteps,
  useGoToFirstUnfinishedStep,
  useIframeMessaging
} from "../../hooks";

import { StepContent } from "./StepContent";
import {
  SubmissionDisclosureSubStep,
  CombinedIncomeSubStep
} from "../../constants";
import { combinedIncomeScreenFlows } from "../../slices/CombinedIncome/constants";
import { shouldSkipSupervisorDetails } from "../../slices/CombinedIncome/utils";
import { getFileUploadErrorMessage } from "shared/utils";

export const ManualApplicationFlow = ({
  setOpen,
  applicant,
  verificationsMeta,
  isApplicationRefreshing,
  refetchApplication,
  application,
  requiredYearsHousing,
  partnerNameInSubmissionDisclosure
}: {
  setOpen: (open: boolean) => void;
  applicant: Application["applicants"][number];
  verificationsMeta: UseQueryResult<
    {
      verifications: Verification[];
    },
    Error
  >;
  isApplicationRefreshing: boolean;
  refetchApplication: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<Application, Error>>;
  application: Application;
  requiredYearsHousing: number;
  partnerNameInSubmissionDisclosure?: string;
}) => {
  const applicantId = applicant.id;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const queryClient = useQueryClient();
  // Iframe loading state
  const [loading, setLoading] = useState(true);
  // Stepper
  const [activeStepName, setActiveStepName] = useState<string | null>(null);

  const [submissionDisClosureSubStep, setSubmissionDisclosureSubStep] =
    useState<SubmissionDisclosureSubStep>(
      SubmissionDisclosureSubStep.SelectMethod
    );

  // If user finished self reported income and SELECTED YES
  const hasIncomeSource =
    (
      applicant.verifications
        ?.find(
          (verification) =>
            verification.name === VerificationNames.IncomeVerification
        )
        ?.steps?.find((step) => step.name === "income_verification") as
        | Step
        | undefined
    )?.data.do_you_have_source_of_income === "Yes";

  const { mutateAsync: updatePreferredCheckoutType } = useMutation({
    mutationKey: ["updatePreferredCheckoutType", application.id, applicantId],
    mutationFn: (data: {
      preferred_checkout_type: CheckoutSubmissionType | null;
    }) =>
      instance.patch(
        `/portal/v1/applications/${application.id}/applicants/${applicantId}/update_preferred_manual_values`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "applicationMeta",
          String(application.id),
          String(applicantId)
        ]
      });
    }
  });

  const { allSteps, stepNames } = useCurrentSteps({
    applicationId: application.id,
    applicantId,
    metaVerificationsData: verificationsMeta.data?.verifications,
    metaVerificationsIsFetching: verificationsMeta.isFetching,
    hasIncomeSource,
    activeStepName,
    configuration: {
      preferredCheckoutType: applicant.preferred_checkout_type,
      preferredSubmissionType: applicant.preferred_submission_disclosure_type
    }
  });

  // Configuration placeholder: add additional options here if needed
  useGoToFirstUnfinishedStep({
    allSteps,
    activeStepName,
    setActiveStepName,
    applicant,
    moveInDate: application.desired_move_in_date
  });

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const verificationId = allSteps[activeStepName || ""]?.id;

  const updateStepMeta = useMutation({
    mutationKey: [
      "updateStepMeta",
      application.id,
      applicantId,
      activeStepName,
      verificationId
    ],
    mutationFn: (data: unknown) =>
      instance.patch(
        `/portal/v1/applications/${application.id}/verifications/${verificationId}/steps/${activeStepName}?applicant_id=${applicantId}`,
        { data }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", String(application.id)]
      });
      verificationsMeta.refetch();
    }
  });

  /**
   * The address step PATCH only saves housing history as a draft (BSCPF-6080);
   * like AX, the manual app has to finish the verification explicitly.
   */
  const finishVerification = useMutation({
    mutationKey: [
      "FINISH_VERIFICATION",
      "UPDATE",
      application.id,
      applicantId,
      verificationId
    ],
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${application.id}/verifications/${verificationId}/finish?applicant_id=${applicantId}`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", String(application.id)]
      });
      verificationsMeta.refetch();
    }
  });

  // Helper functions to reduce cognitive complexity
  const validateHousingStep = (formData: object) => {
    if (activeStepName !== "address") return true;

    const ret = validateHousing(formData, requiredYearsHousing * 12);
    if (!ret) return true;

    if (ret.errors.length) {
      toast.error(ret.errors[0].message);
      return false;
    }

    if (!ret.hasFullRequiredYears) {
      toast.error(
        `${requiredYearsHousing} year${requiredYearsHousing > 1 ? "s" : ""} of housing history is required`
      );
      return false;
    }

    return true;
  };

  const processFormData = async (formData: Record<string, unknown>) => {
    const dataUrlFiles: DataUrlItem[] = [];
    findDataUrls(formData, [], dataUrlFiles);

    const links = dataUrlFiles.length
      ? await uploadLinks(dataUrlFiles, String(applicantId))
      : [];

    return links.reduce((data, [path, gid]) => set(data, path, gid), {
      ...formData
    });
  };

  const getNextStepForIncome = async (
    patchedData: { do_you_have_source_of_income: string },
    currentIndex: number
  ) => {
    await verificationsMeta.refetch();
    await refetchApplication();

    const saidNoIncome = patchedData.do_you_have_source_of_income === "No";
    const hasVerifiedIncome = verificationsMeta.data?.verifications.some(
      (verification) =>
        verification.name === VerificationNames.VerifiedIncomeVerification
    );

    if (saidNoIncome)
      return applicant.preferred_checkout_type === SubmissionType.SendByEmail
        ? undefined
        : "checkout";

    return hasVerifiedIncome ? "income_documents" : stepNames[currentIndex + 1];
  };

  const combinedIncomeVerification = useMemo(
    () =>
      verificationsMeta.data?.verifications.find(
        (verification) => verification.name === VerificationNames.CombinedIncome
      ),
    [verificationsMeta.data?.verifications]
  );

  const combinedIncomeStep = useMemo(
    () =>
      combinedIncomeVerification?.steps?.find(
        (step) => step.name === "combined_income"
      ),
    [combinedIncomeVerification?.steps]
  );

  const combinedIncomeSubStep = useMemo(() => {
    return (
      (combinedIncomeStep?.data.lastStep as
        | CombinedIncomeSubStep
        | undefined) ?? CombinedIncomeSubStep.IncomeStatus
    );
  }, [combinedIncomeStep?.data.lastStep]);

  const hasCombinedIncome = useMemo(() => {
    if (!combinedIncomeVerification?.incomes?.length) return false;

    return combinedIncomeVerification.incomes.some(
      (income) =>
        ![IncomeStatus.Deleted, IncomeStatus.Pending].includes(income.status) &&
        income.type !== IncomeType.NoIncome
    );
  }, [combinedIncomeVerification?.incomes]);

  const skipSupervisorDetails = useMemo(() => {
    const verificationSchema = combinedIncomeVerification?.settings as
      | CombinedIncomeVerificationSchema
      | undefined;
    const currentIncomeType = combinedIncomeStep?.data?.currentIncomeType as
      | IncomeType
      | undefined;
    return shouldSkipSupervisorDetails(verificationSchema, currentIncomeType);
  }, [combinedIncomeVerification?.settings, combinedIncomeStep?.data]);

  const backCombinedIncomeSubStep = useMemo(() => {
    const stepData = combinedIncomeStep?.data;
    if (!stepData?.lastStep) return undefined;

    const flowConfig = combinedIncomeScreenFlows[combinedIncomeSubStep];
    const backFlow = flowConfig.back;

    if (!backFlow) return undefined;

    if (!Array.isArray(backFlow)) return backFlow;

    const currentIncomeType = stepData.currentIncomeType as IncomeType;
    const matchedCondition = backFlow.find((flow) =>
      flow.condition({
        incomeType: currentIncomeType,
        hasIncome: hasCombinedIncome,
        skipSupervisorDetails
      })
    );
    return matchedCondition?.target;
  }, [combinedIncomeStep?.data, hasCombinedIncome, skipSupervisorDetails]);

  const navigateToNextStep = async (nextStepName: string | undefined) => {
    if (!nextStepName) {
      setOpen(false);
      return;
    }

    if (nextStepName === "checkout") await refetchApplication();

    setActiveStepName(nextStepName);
  };

  const handleCheckoutGoBack = () => {
    if (
      [SubmissionType.InPerson, SubmissionType.CheckoutPaidInCash].includes(
        applicant.preferred_checkout_type as SubmissionType
      )
    )
      //back to checkout type selection step
      return updatePreferredCheckoutType({
        preferred_checkout_type: null
      }).catch(() => {
        toast.error("An error occurred while going back");
      });

    const prevIdx = stepNames.length - 2;
    if (!prevIdx) return;

    if (stepNames[prevIdx] === "verified_income") {
      // check if income_verification is not no
      const incomeVerificationStep = allSteps["income_verification"];
      const incomeVerificationData = incomeVerificationStep.data;
      const hasIncomeSource =
        incomeVerificationData.do_you_have_source_of_income === "Yes";
      if (!hasIncomeSource) setActiveStepName(stepNames[prevIdx]);
      else setActiveStepName(stepNames[prevIdx - 1]);
    } else if (prevIdx >= 0) setActiveStepName(stepNames[prevIdx]);
  };

  const handleCombinedIncomeGoBack = async () => {
    if (
      !backCombinedIncomeSubStep ||
      combinedIncomeSubStep === CombinedIncomeSubStep.IncomeStatus
    )
      return;

    updateStepMeta.mutateAsync(
      { lastStep: backCombinedIncomeSubStep },
      {
        onError: (error) => {
          toast.error(
            getErrorMessage(error) || "Failed to update step on back"
          );
        }
      }
    );
  };

  const handleGoBack = async () => {
    if (activeStepName === "checkout") return handleCheckoutGoBack();

    if (
      activeStepName === "submission_disclosure_step" &&
      [
        SubmissionDisclosureSubStep.ReviewAndSendEmail,
        SubmissionDisclosureSubStep.ReviewAndSign,
        SubmissionDisclosureSubStep.SignOnBehalf
      ].includes(submissionDisClosureSubStep)
    ) {
      setSubmissionDisclosureSubStep(SubmissionDisclosureSubStep.SelectMethod);
      return;
    }

    if (
      activeStepName === "combined_income" &&
      combinedIncomeSubStep !== CombinedIncomeSubStep.IncomeStatus
    ) {
      await handleCombinedIncomeGoBack();
      return;
    }

    const nextIdx = stepNames.indexOf(activeStepName!) - 1;

    if (nextIdx >= 0) {
      setActiveStepName(stepNames[nextIdx]);
      if (stepNames[nextIdx] === "submission_disclosure_step")
        setSubmissionDisclosureSubStep(
          SubmissionDisclosureSubStep.SelectMethod
        );
    }
  };

  useIframeMessaging({
    iframeRef,
    activeStepName,
    allSteps,
    partnerNameInSubmissionDisclosure,
    applicationId: application.id,
    onSubmit: async (formData) => {
      try {
        if (!validateHousingStep(formData)) return;

        const patchedData = await processFormData(formData);
        await updateStepMeta.mutateAsync(patchedData);
        if (activeStepName === "address")
          await finishVerification.mutateAsync();

        if (!activeStepName) return;

        const currentIndex = stepNames.indexOf(activeStepName);
        const nextStepName =
          activeStepName === "income_verification"
            ? await getNextStepForIncome(
                patchedData as { do_you_have_source_of_income: string },
                currentIndex
              )
            : stepNames[currentIndex + 1];

        await navigateToNextStep(nextStepName);
      } catch (error) {
        toast.error(getFileUploadErrorMessage(error) || getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    setLoading
  });

  useEffect(() => {
    const submissionDisclosureVerification = applicant.verifications?.find(
      (verification) =>
        verification.name === VerificationNames.SubmissionDisclosure
    );
    const submissionDisclosureStep =
      submissionDisclosureVerification?.steps?.find(
        (step) => step.name === "submission_disclosure_step"
      );
    if (Object.keys(submissionDisclosureStep?.data || {}).length > 0) {
      setSubmissionDisclosureSubStep(SubmissionDisclosureSubStep.Signed);
      return;
    }

    const type = applicant.preferred_submission_disclosure_type;

    if (type === SubmissionType.SendByEmail) {
      const submissionDisclosureVerification = applicant.verifications?.find(
        (verification) =>
          verification.name === VerificationNames.SubmissionDisclosure
      );

      if (
        submissionDisclosureVerification?.has_active_submission_disclosure_link
      ) {
        setSubmissionDisclosureSubStep(SubmissionDisclosureSubStep.EmailSent);
        return;
      }

      setSubmissionDisclosureSubStep(
        applicant.manual_email
          ? SubmissionDisclosureSubStep.ReviewAndSendEmail
          : SubmissionDisclosureSubStep.SelectMethod
      );
      return;
    }

    if (type === SubmissionType.InPerson)
      setSubmissionDisclosureSubStep(SubmissionDisclosureSubStep.ReviewAndSign);

    if (type === SubmissionType.SignOnBehalf)
      setSubmissionDisclosureSubStep(SubmissionDisclosureSubStep.SignOnBehalf);
  }, [allSteps, applicant]);

  if (
    // !allSteps ||
    !stepNames.length ||
    !activeStepName
  )
    return null;

  return (
    <div className="flex divide-x overflow-hidden rounded-lg">
      <Aside
        applicant={applicant}
        stepNames={stepNames}
        allSteps={allSteps}
        isSubmissionSentByEmail={
          applicant.preferred_submission_disclosure_type ===
          SubmissionType.SendByEmail
        }
        activeStepName={activeStepName}
        desiredMoveInDate={application.desired_move_in_date}
      />
      <IframeLayout
        goBack={handleGoBack}
        activeStepName={activeStepName}
        setOpen={setOpen}
        setActiveStepName={setActiveStepName}
        stepNames={stepNames}
        noBack={
          (activeStepName === "submission_disclosure_step" &&
            [
              SubmissionDisclosureSubStep.SelectMethod,
              SubmissionDisclosureSubStep.EmailSent,
              SubmissionDisclosureSubStep.Signed
            ].includes(submissionDisClosureSubStep)) ||
          (activeStepName === "checkout" &&
            applicant.verifications?.some(
              (v) => v.name === VerificationNames.CombinedIncome
            )) ||
          (activeStepName === "combined_income" &&
            !backCombinedIncomeSubStep &&
            combinedIncomeSubStep !== CombinedIncomeSubStep.IncomeStatus)
        }
      >
        <StepContent
          activeStepName={activeStepName}
          iframeRef={iframeRef}
          loading={loading}
          setLoading={setLoading}
          setOpen={setOpen}
          setActiveStepName={setActiveStepName}
          stepNames={stepNames}
          allSteps={allSteps}
          isApplicationRefreshing={isApplicationRefreshing}
          refetchApplication={refetchApplication}
          application={application}
          applicant={applicant}
          hasIncomeSource={hasIncomeSource}
          verificationsMeta={verificationsMeta}
          queryClient={queryClient}
          submissionDisclosureSubStep={submissionDisClosureSubStep}
          setSubmissionDisclosureSubStep={setSubmissionDisclosureSubStep}
        />
      </IframeLayout>
    </div>
  );
};
